import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Tabs, Table, Button, message, Modal, Tag, Space } from 'antd';
import type { TabsProps } from 'antd';
import type { TableRowSelection } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GET_DRIVERS_BY_VERIFICATION_STATUS, GET_DRIVER_VERIFICATION_HISTORY } from '../../../graphql/queries/driverVerification/getDriverVerificationQueries';
import { GET_DRIVER_VERIFICATION_TAB_COUNTS, GetDriverVerificationTabCountsResponse } from '../../../graphql/queries/driverVerification/getDriverVerificationTabCounts';
import DriverVerificationFilters from './components/DriverVerificationFilters';
import { columns } from './components/DriverVerificationTableColumns';
import { useVehicleTypeOptions } from '../../../hooks/useVehicleTypeOptions';
import BatchReviewDrawer from './components/BatchReviewDrawer';
import { DriverVerificationInfoType } from '../../../graphql/types/driverVerification/DriverVerificationTypes';
import TablePaginationBar from '../../../components/TablePaginationBar';

// 审核状态：1-待审核，2-审核通过，3-审核不通过
const VERIFICATION_STATUS = {
    PENDING: 1,
    APPROVED: 2,
    REJECTED: 3,
};

// 筛选/翻页触发查询的防抖延迟
const LOAD_DATA_DEBOUNCE_MS = 300;

const DriverVerificationListPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const vehicleTypeOptions = useVehicleTypeOptions();
    
    // 从URL参数恢复筛选状态
    const getInitialFilters = () => {
        const driverId = searchParams.get('driverId');
        const driverName = searchParams.get('driverName');
        const driverPhone = searchParams.get('driverPhone');
        const dspId = searchParams.get('dspId');
        const tab = searchParams.get('tab');
        const page = searchParams.get('page');
        const pageSizeParam = searchParams.get('pageSize');
        
        return {
            filters: {
                driverId: driverId ? Number(driverId) : undefined,
                driverName: driverName || undefined,
                driverPhone: driverPhone || undefined,
                dspId: dspId ? Number(dspId) : undefined,
            },
            activeTab: tab || 'pending',
            currentPage: page ? Number(page) : 1,
            pageSize: pageSizeParam ? Number(pageSizeParam) : 20,
        };
    };
    
    const initialState = getInitialFilters();
    const [activeTab, setActiveTab] = useState<string>(initialState.activeTab);
    const [filters, setFilters] = useState<{
        driverId?: number;
        driverName?: string;
        driverPhone?: string;
        dspId?: number;
    }>(initialState.filters);
    const [currentPage, setCurrentPage] = useState(initialState.currentPage);
    const [pageSize, setPageSize] = useState(initialState.pageSize);
    const [historyModalVisible, setHistoryModalVisible] = useState(false);
    const [selectedDriverAccountId, setSelectedDriverAccountId] = useState<number | null>(null);
    // 批量审核相关状态
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [batchReviewVisible, setBatchReviewVisible] = useState(false);
    
    const [getDrivers, { data, loading, refetch }] = useLazyQuery(GET_DRIVERS_BY_VERIFICATION_STATUS, {
        fetchPolicy: 'network-only', // 总是从网络获取最新数据，避免缓存导致的语言显示问题
    });
    
    // 查询审核历史
    const { data: historyData, loading: historyLoading } = useQuery(GET_DRIVER_VERIFICATION_HISTORY, {
        variables: {
            driverAccountId: selectedDriverAccountId || 0,
            pageRequest: {
                page: 1,
                size: 100,
            },
        },
        skip: !selectedDriverAccountId || !historyModalVisible,
        fetchPolicy: 'network-only',
    });
    
    // 使用 useMemo 创建稳定的 filters 字符串用于依赖比较（列表查询防抖、统计查询防抖共用）
    const filtersKey = useMemo(() => {
        return JSON.stringify(filters);
    }, [filters.driverId, filters.driverName, filters.driverPhone, filters.dspId]);

    // 构建统计查询的筛选条件
    const tabCountsFilters = useMemo(() => {
        return {
            driverId: filters.driverId,
            driverName: filters.driverName,
            driverPhone: filters.driverPhone,
            dspId: filters.dspId,
        };
    }, [filters]);

    // 统计查询的筛选条件也做防抖，跟下方司机列表查询保持同一节奏（300ms），
    // 否则 useQuery 的 variables 一变（每敲一个字符）就会立刻发起统计接口请求，脱离防抖
    const [debouncedTabCountsFilters, setDebouncedTabCountsFilters] = useState(tabCountsFilters);
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedTabCountsFilters(tabCountsFilters), LOAD_DATA_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [filtersKey]);

    // 查询Tab统计数量
    const { data: countsData, loading: countsLoading, refetch: refetchCounts } = useQuery<GetDriverVerificationTabCountsResponse>(
        GET_DRIVER_VERIFICATION_TAB_COUNTS,
        {
            variables: {
                filters: debouncedTabCountsFilters,
            },
            errorPolicy: 'all',
            fetchPolicy: 'network-only', // 总是从网络获取最新数据，避免缓存问题
        }
    );
    
    // Tab统计数量
    const tabCounts = useMemo(() => {
        return {
            pending: countsData?.getDriverVerificationTabCounts?.pending || 0,
            approved: countsData?.getDriverVerificationTabCounts?.approved || 0,
            rejected: countsData?.getDriverVerificationTabCounts?.rejected || 0,
        };
    }, [countsData]);
    
    // 根据Tab获取对应的审核状态
    const getVerificationStatus = (tabKey: string): number => {
        switch (tabKey) {
            case 'pending':
                return VERIFICATION_STATUS.PENDING;
            case 'approved':
                return VERIFICATION_STATUS.APPROVED;
            case 'rejected':
                return VERIFICATION_STATUS.REJECTED;
            default:
                return VERIFICATION_STATUS.PENDING;
        }
    };
    
    // 加载数据
    const loadData = () => {
        const verificationStatus = getVerificationStatus(activeTab);
        // 根据审核状态选择排序字段：待审核用修改时间，审核通过/不通过用审核时间
        const sortBy = activeTab === 'pending' ? 'updatedAt' : 'verificationTime';
        getDrivers({
            variables: {
                verificationStatus,
                filters: {
                    driverId: filters.driverId,
                    driverName: filters.driverName,
                    driverPhone: filters.driverPhone,
                    dspId: filters.dspId,
                },
                pageRequest: {
                    page: currentPage,
                    size: pageSize,
                    sortBy: sortBy,
                    sortDirection: 'DESC',
                },
            },
        });
    };
    
    // 触发查询：首次挂载、切换Tab、翻页都立即查询；只有筛选条件变化（filtersKey 变化）才防抖，
    // 避免逐字符输入时连续发请求。用 ref 记录上一次的 filtersKey，据此判断这次触发是不是筛选条件引起的——
    // 即使一次操作里筛选条件和页码同时变化（如筛选时重置到第1页），也只会走这一个 effect 一次，不会重复查询
    const isFirstLoadRef = useRef(true);
    const prevFiltersKeyRef = useRef(filtersKey);
    useEffect(() => {
        const filtersChanged = prevFiltersKeyRef.current !== filtersKey;
        prevFiltersKeyRef.current = filtersKey;

        if (isFirstLoadRef.current) {
            isFirstLoadRef.current = false;
            loadData();
            return;
        }

        if (!filtersChanged) {
            // 仅Tab切换/翻页：立即查询
            loadData();
            return;
        }

        // 筛选条件变化：防抖，effect 的 cleanup 会在下一次依赖变化或卸载时自动清掉上一个未到期的定时器
        const timer = setTimeout(loadData, LOAD_DATA_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [activeTab, currentPage, pageSize, filtersKey]);
    
    // 更新URL参数
    const updateSearchParams = (newFilters: typeof filters, newTab?: string, newPage?: number, newPageSize?: number) => {
        const params = new URLSearchParams();
        if (newFilters.driverId) params.set('driverId', String(newFilters.driverId));
        if (newFilters.driverName) params.set('driverName', newFilters.driverName);
        if (newFilters.driverPhone) params.set('driverPhone', newFilters.driverPhone);
        if (newFilters.dspId) params.set('dspId', String(newFilters.dspId));
        if (newTab || activeTab) params.set('tab', newTab || activeTab);
        if (newPage || currentPage) params.set('page', String(newPage || currentPage));
        if (newPageSize || pageSize) params.set('pageSize', String(newPageSize || pageSize));
        setSearchParams(params, { replace: true });
    };
    
    // 处理筛选
    // 查询由下方 useEffect（监听 filtersKey/currentPage）统一触发，这里只更新状态，避免重复查询
    const handleFilter = (newFilters: {
        driverId?: number;
        driverName?: string;
        driverPhone?: string;
        dspId?: number;
    }) => {
        setFilters(newFilters);
        setCurrentPage(1);
        updateSearchParams(newFilters, undefined, 1);
    };

    // 处理重置筛选
    // 查询由下方 useEffect（监听 filtersKey/currentPage）统一触发，这里只更新状态，避免重复查询
    const handleResetFilters = () => {
        const resetFilters = {
            driverId: undefined,
            driverName: undefined,
            driverPhone: undefined,
            dspId: undefined,
        };
        setFilters(resetFilters);
        setCurrentPage(1);
        // 清空URL参数
        setSearchParams({}, { replace: true });
    };
    
    // 处理审核操作 - 跳转到详情页，保存筛选状态到URL
    const handleReview = (driverAccountId: number) => {
        const params = new URLSearchParams();
        if (filters.driverId) params.set('driverId', String(filters.driverId));
        if (filters.driverName) params.set('driverName', filters.driverName);
        if (filters.driverPhone) params.set('driverPhone', filters.driverPhone);
        if (filters.dspId) params.set('dspId', String(filters.dspId));
        params.set('tab', activeTab);
        params.set('page', String(currentPage));
        params.set('pageSize', String(pageSize));
        const queryString = params.toString();
        navigate(`/qualitycontrol/driver-verification/${driverAccountId}${queryString ? `?${queryString}` : ''}`);
    };
    
    // 处理查看日志
    const handleViewLog = (driverAccountId: number) => {
        setSelectedDriverAccountId(driverAccountId);
        setHistoryModalVisible(true);
    };
    
    // 审核历史表格列
    const historyColumns = [
        {
            title: t('riskControl.driverVerification.historyModal.operationTime'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text: string) => new Date(text).toLocaleString(),
        },
        {
            title: t('riskControl.driverVerification.historyModal.operator'),
            dataIndex: 'reviewerName',
            key: 'reviewerName',
        },
        {
            title: t('riskControl.driverVerification.historyModal.operationResult'),
            dataIndex: 'verificationStatus',
            key: 'verificationStatus',
            render: (status: number) => {
                const statusMap: Record<number, { text: string; color: string }> = {
                    2: { text: t('enum.driverVerificationReviewResult.APPROVED') as string, color: 'green' },
                    3: { text: t('enum.driverVerificationReviewResult.REJECTED') as string, color: 'red' },
                };
                const statusInfo = statusMap[status] || { text: '-', color: 'default' };
                return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
            },
        },
        {
            title: t('riskControl.driverVerification.historyModal.comment'),
            dataIndex: 'reviewComment',
            key: 'reviewComment',
            render: (text: string) => text || '-',
        },
    ];
    
    // Tab配置
    const tabItems: TabsProps['items'] = [
        {
            key: 'pending',
            label: `${t('riskControl.driverVerification.pending')} (${countsLoading ? '...' : tabCounts.pending})`,
            children: null,
        },
        {
            key: 'approved',
            label: `${t('riskControl.driverVerification.approved')} (${countsLoading ? '...' : tabCounts.approved})`,
            children: null,
        },
        {
            key: 'rejected',
            label: `${t('riskControl.driverVerification.rejected')} (${countsLoading ? '...' : tabCounts.rejected})`,
            children: null,
        },
    ];
    
    // 表格数据
    const tableData = data?.getDriversByVerificationStatus?.content || [];
    const totalElements = data?.getDriversByVerificationStatus?.totalElements || 0;
    
    // 表格列配置
    const tableColumns = columns(t, activeTab, handleReview, handleViewLog, vehicleTypeOptions);
    
    // 审核历史数据
    const history = historyData?.getDriverVerificationHistory?.content || [];
    
    // 批量选择配置（仅在待审核tab显示）
    const rowSelection: TableRowSelection<DriverVerificationInfoType> | undefined = activeTab === 'pending' ? {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(newSelectedRowKeys);
        },
        getCheckboxProps: (record) => ({
            name: record.driverAccountId.toString(),
        }),
    } : undefined;
    
    // 处理批量审核
    const handleBatchReview = () => {
        if (selectedRowKeys.length === 0) {
            message.warning(t('riskControl.driverVerification.messages.selectDriversForBatchReview'));
            return;
        }
        setBatchReviewVisible(true);
    };
    
    // 批量审核完成后的回调
    const handleBatchReviewComplete = () => {
        setSelectedRowKeys([]);
        refetch(); // 刷新列表
        refetchCounts(); // 刷新统计
    };
    
    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            {/* 筛选条件 */}
            <DriverVerificationFilters
                filters={filters}
                onFilter={handleFilter}
                onReset={handleResetFilters}
            />
            
            {/* Tab切换和批量审核按钮 */}
            <div className="flex items-center justify-between mb-4">
                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => {
                        setActiveTab(key);
                        setCurrentPage(1);
                        setSelectedRowKeys([]); // 切换tab时清空选择
                        updateSearchParams(filters, key, 1);
                    }}
                    items={tabItems}
                    className="[&_.ant-tabs-tab]:font-bold"
                />
                {activeTab === 'pending' && (
                    <Space>
                        <span className="text-gray-900 font-bold">
                            {t('riskControl.driverVerification.batchReview.selectedCount', {
                                count: selectedRowKeys.length,
                            })}
                        </span>
                        <Button
                            type="primary"
                            onClick={handleBatchReview}
                            disabled={selectedRowKeys.length === 0}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('riskControl.driverVerification.batchReview.startBatchReview')}</span>
                        </Button>
                    </Space>
                )}
            </div>
            
            {/* 列表表格 */}
            <Table
                rowKey="driverAccountId"
                rowSelection={rowSelection}
                columns={tableColumns}
                dataSource={tableData}
                loading={loading}
                pagination={false}
             className="mb-4"/>

            {/* 分页 */}
            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                    updateSearchParams(filters, undefined, page, size);
                }}
                onShowSizeChange={(_, size) => {
                    setCurrentPage(1);
                    setPageSize(size);
                    updateSearchParams(filters, undefined, 1, size);
                }}
                pageSizeOptions={['10', '20', '50', '100']}
            />
            
            {/* 批量审核抽屉 */}
            <BatchReviewDrawer
                visible={batchReviewVisible}
                driverAccountIds={selectedRowKeys.map(key => Number(key))}
                onClose={() => {
                    setBatchReviewVisible(false);
                    setSelectedRowKeys([]);
                }}
                onComplete={handleBatchReviewComplete}
            />
            
            {/* 审核历史弹窗 */}
            <Modal
                title={t('riskControl.driverVerification.historyModal.title') as string}
                open={historyModalVisible}
                onCancel={() => {
                    setHistoryModalVisible(false);
                    setSelectedDriverAccountId(null);
                }}
                footer={null}
                width={800}
            >
                <Table
                    columns={historyColumns}
                    dataSource={history}
                    rowKey="id"
                    pagination={false}
                    loading={historyLoading}
                 className="mb-4"/>
            </Modal>
        </div>
    );
};

export default DriverVerificationListPage;

